import React, { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { LayoutDashboard, Plus, Check, X, Search } from 'lucide-react';
import { T } from '../../components/em/ui';

/**
 * Widget catalogue — a slide-in drawer (opened from the dashboard's edit-mode
 * toolbar) that lets users browse every widget allowed for their role and add
 * hidden ones back in-context. "Adding" simply un-hides; "removing" hides — the
 * layout item itself is never dropped, so a re-added widget returns to its spot.
 */
const WidgetCatalog = ({ widgets = [], hidden = [], onAdd, onRemove, onClose }) => {
  const [query, setQuery] = useState('');

  const hiddenSet = useMemo(() => new Set(hidden), [hidden]);
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return widgets;
    return widgets.filter(
      (w) =>
        w.title.toLowerCase().includes(q) ||
        (w.description || '').toLowerCase().includes(q)
    );
  }, [widgets, query]);

  const available = filtered.filter((w) => hiddenSet.has(w.id));
  const onBoard = filtered.filter((w) => !hiddenSet.has(w.id));

  const row = (w) => {
    const isHidden = hiddenSet.has(w.id);
    const Icon = w.icon || LayoutDashboard;
    return (
      <div key={w.id} className="dash-cat-card">
        <div
          className="dash-cat-icon"
          style={{ background: isHidden ? T.bgHead : T.orange50 }}
        >
          <Icon size={18} color={isHidden ? T.fg7 : T.orange} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ font: `600 13px ${T.font}`, color: T.fg1 }}>{w.title}</div>
          <div
            style={{
              font: `400 11.5px ${T.font}`,
              color: T.fg7,
              marginTop: 1,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {w.description || w.id}
          </div>
        </div>
        {isHidden ? (
          <button
            type="button"
            className="dash-cat-add"
            onClick={() => onAdd(w.id)}
            title={`Add ${w.title}`}
          >
            <Plus size={15} /> Add
          </button>
        ) : (
          <button
            type="button"
            className="dash-cat-remove"
            onClick={() => onRemove(w.id)}
            title={`Remove ${w.title}`}
          >
            <Check size={14} /> Added
          </button>
        )}
      </div>
    );
  };

  // Portal to <body> so the drawer escapes the dashboard's stacking context
  // (its transforms/filters would otherwise trap a fixed overlay below the nav).
  return createPortal(
    <div className="dash-cat-overlay" onClick={onClose}>
      <aside
        className="dash-cat-drawer"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="Widget catalogue"
      >
        <header className="dash-cat-head">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div className="dash-cat-head-icon">
              <LayoutDashboard size={18} color={T.orange} />
            </div>
            <div>
              <div style={{ font: `700 15px ${T.font}`, color: T.fg1 }}>
                Add a widget
              </div>
              <div style={{ font: `400 11.5px ${T.font}`, color: T.fg5 }}>
                {available.length} available · {onBoard.length} on your dashboard
              </div>
            </div>
          </div>
          <button
            type="button"
            className="dash-cat-close"
            onClick={onClose}
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </header>

        <div className="dash-cat-search">
          <Search size={15} color={T.fg7} />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search widgets…"
          />
        </div>

        <div className="dash-cat-body">
          {available.length > 0 && (
            <>
              <div className="dash-cat-section">Available to add</div>
              {available.map(row)}
            </>
          )}
          {available.length === 0 && (
            <div className="dash-cat-empty">
              {query
                ? 'No matching widgets.'
                : 'Every widget for your role is already on the dashboard.'}
            </div>
          )}
          {onBoard.length > 0 && (
            <>
              <div className="dash-cat-section" style={{ marginTop: 14 }}>
                On your dashboard
              </div>
              {onBoard.map(row)}
            </>
          )}
        </div>
      </aside>
    </div>,
    document.body
  );
};

export default WidgetCatalog;
